/**
 * Lightweight API reachability check before redirecting users to backend /auth/login.
 * Avoids landing on Railway/proxy HTML error pages when the backend is down.
 *
 * We use GET /api/auth/me (same origin+CORS policy as login) rather than GET /health: the
 * health_bypass_middleware responds to /health before CORSMiddleware, so browsers block
 * cross-origin reads — fetch always fails from the SPA.
 */
import * as Sentry from '@sentry/react';
import { API_BASE_URL } from '@/config';

const HEALTH_CHECK_TIMEOUT_MS = 5000;

/** Reachability probe: must go through FastAPI middleware so CORS headers are present for the SPA. */
export function getAuthMeCheckUrl(apiBaseUrl: string = API_BASE_URL): string {
  const trimmed = apiBaseUrl.replace(/\/+$/, '');
  return `${trimmed}/auth/me`;
}

/**
 * Returns true when we get any HTTP response the browser exposes (proving API+CORS reachable).
 */
export async function isApiReachable(apiBaseUrl: string = API_BASE_URL): Promise<boolean> {
  const url = getAuthMeCheckUrl(apiBaseUrl);
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), HEALTH_CHECK_TIMEOUT_MS);
  try {
    await fetch(url, {
      method: 'GET',
      credentials: 'include',
      signal: controller.signal,
    });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function reportApiUnreachableAtLogin(context?: Record<string, string>): void {
  try {
    Sentry.captureMessage('API unreachable at login', {
      level: 'warning',
      extra: {
        probeUrl: getAuthMeCheckUrl(),
        apiBaseUrl: API_BASE_URL,
        ...context,
      },
    });
  } catch {
    // Sentry not initialized — ignore
  }
}

export type AuthLoginRedirectResult = 'redirected' | 'unavailable';

/** Health pre-check; redirects to backend login only when the API is up. */
export async function redirectToAuthLogin(
  context?: Record<string, string>
): Promise<AuthLoginRedirectResult> {
  const reachable = await isApiReachable();
  if (!reachable) {
    reportApiUnreachableAtLogin(context);
    return 'unavailable';
  }
  window.location.href = `${API_BASE_URL}/auth/login`;
  return 'redirected';
}
